import * as React from 'react'
import PropTypes from 'prop-types'
import {
  Tabs,
  Button,
  Tab,
  Typography,
  Box,
  Tooltip,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material'
import TaskPanel from '../topBar/Task-UnitTest-Panel'
import StudentLayoutTaskPanel from '../session/studentView/StudentLayoutTaskPanel'
import { studentPathFor } from '../session/studentView/studentSurface'
import {
  aiChatEnabled,
  buildAiChatUpdate,
  describeSaveFailure,
  isPythonFamily,
  saveSessionFields,
} from '../session/sessionSettings'
import GroupPanel from '../group/GroupPanel'
import { SessionContext } from '../../context/SessionContext'
import RawDataModal from './RawDataPanel'
import InstructorDashboardPanel from '../instructorDashboard/InstructorDashboardPanel'
import { useContext, useEffect, useState } from 'react'
import { showToast } from '../commonUnit/Toast'
import { ContentCopy, DataObject } from '@mui/icons-material'
import { AuthContext } from '../../context/AuthContext'
import {
  groupingInSession,
  setSessionRegrouping,
  setSessionGroupRound,
  setSessionEnableChat,
  setSessionEnableChatAndCloseSession,
} from '../../service/sessionService'
import { SubmissionsContext } from '../../context/SubmissionsContext'
import {
  getPassrateOfClass,
  getTotalPassrateOfClass,
} from '../../service/submissionService'
import AudioGroupPanel from '../audioSTU/group/AudioGroupPanel'
import CustumPromptPanel from '../chat/AIChat/CustomPromptPanel'
import { AutoGroupingContext } from '../../context/AutoGroupingContext'
import { CurrentStepContext } from '../../context/CurrentStepContext'

function CustomTabPanel(props) {
  const { children, value, index, keepMounted, ...other } = props
  const isActive = value === index

  // keepMounted: for panels holding local state that shouldn't reset every time the
  // instructor switches to a different tab and back (e.g. the Session Dashboard's session
  // timer, which lives in React state with no server-side persistence) - render always,
  // hide via CSS instead of unmounting. Every other tab keeps its original
  // conditionally-rendered behavior.
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {keepMounted ? (
        <Box style={{ display: isActive ? 'block' : 'none' }}>
          <Typography component="div">{children}</Typography>
        </Box>
      ) : (
        isActive && (
          <Box>
            {/* component="div", as in the keepMounted branch above: a bare Typography
                renders a <p>, and every tab here puts block content (divs, tables, editors)
                inside it, which is invalid nesting (React warns on every render). */}
            <Typography component="div">{children}</Typography>
          </Box>
        )
      )}
    </div>
  )
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  keepMounted: PropTypes.bool,
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

export default function BasicTabs() {
  // The selected tab is remembered by its label, not its position: the tab list changes
  // shape at runtime (Groups exists only for non-Vizmental sessions, so turning AI chat on
  // for a Python session removes it). Tracking an index then pointed at the wrong tab - or
  // past the end - and re-keyed the kept-mounted Monitor panel, resetting its timer.
  const [selectedLabel, setSelectedLabel] = React.useState('Session Setting')
  const { session, setSession, setLink } = useContext(SessionContext)
  const { currentUser } = useContext(AuthContext)
  const { submissions } = useContext(SubmissionsContext)
  const [chatEnable, setChatEnable] = useState(false)
  const [rawDataModalOpen, setRawDataModalOpen] = useState(false)
  const {
    autoGroupingRate,
    setAutoGroupingRate,
    classPassrate,
    setClassPassrate,
  } = useContext(AutoGroupingContext)
  const { setActiveStep } = useContext(CurrentStepContext)

  const Grouping = async () => {
    if (!session.stu_num || session.stu_num <= 2) {
      showToast('Please wait for more students', 'warning')
      return
    }
    await setSessionRegrouping(session.id, true)
    await groupingInSession(session, 'passrate').then((res) => {
      if (res) {
        showToast('Grouping successfully.', 'success')

        setSessionRegrouping(session.id, false)
        setSessionGroupRound(session.id, 1)
      } else {
        showToast('Grouping failed.', 'error')
        setSessionRegrouping(session.id, false)
        return
      }
    })
    await setSessionEnableChat(session.id, true)
  }

  useEffect(() => {
    setChatEnable(session?.enable_chat)
  }, [session])

  const handleNext = async () => {
    if (!session.stu_num || session.stu_num <= 1) {
      showToast('Please wait for more students', 'warning')
      return
    }
    try {
      const res = await groupingInSession(session, 'passrate', 2)
      if (res) {
        showToast('Grouping successfully.', 'success')
        setChatEnable(true)
        setActiveStep(1)
      } else {
        showToast('Grouping failed.', 'error')
      }
    } catch (error) {
      showToast('An error occurred while removing the group.', 'error')
      console.error('Error removing group:', error)
    }

    await setSessionEnableChat(session.id, !chatEnable)
  }

  useEffect(() => {
    console.log(submissions)
    console.log(session)
    if (!submissions) return
    if (session?.type == 'Auto Grouping') {
      let passrate = getPassrateOfClass(
        session.stu_num,
        session?.test_list?.length,
        submissions
      )
      console.log(passrate)
      setClassPassrate(passrate)
      if (
        passrate >= autoGroupingRate &&
        session?.group_round == 0 &&
        !session?.regrouping
      ) {
        if (currentUser.role < 3) {
          Grouping()
        }
      }
    }
  }, [submissions, autoGroupingRate, session])

  const handleCopyLink = async () => {
    // The student page for this session's type (studentSurface.js): /workspace/:id for the
    // Python family, /session/:id - this instructor view's own URL - for the rest.
    const currentUrl = `${window.location.origin}${studentPathFor(session)}`;

    // Copy immediately on user click to avoid permission issues
    try {
      await navigator.clipboard.writeText(currentUrl);
      showToast('Link copied to clipboard!', 'success', 2000);
      setLink(currentUrl);
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast('Failed to copy link', 'error');
      return;
    }

    // Then try to shorten and copy again
    const headers = {
      Authorization:
        'Bearer xVtTD1mVA4CnM5sqCReF7w8GtxjItg2FcdEiiwS4cMl0C5FYQ9vYh9ULMO7E',
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    const body = {
      long_url: currentUrl,
    };

    try {
      const response = await fetch('https://t.ly/api/v1/link/shorten', {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const resp = await response.json();
        await navigator.clipboard.writeText(resp['short_url']);
        setLink(resp['short_url']);
        showToast('Shortened link copied to clipboard!', 'success', 2000);
      }
    } catch (error) {
      console.error('Link shortening failed:', error);
      // Already copied the long URL, so this is fine
    }
  };

  const handleChange = (event, newValue) => {
    setSelectedLabel(tabs[newValue]?.label ?? 'Session Setting')
  }
  // First tab: the exercise laid out exactly as students see it, with in-place editing
  // (StudentLayoutTaskPanel). The original task/test/starter-code editor is kept as the
  // last tab, "Task and Unit Test".
  const tabs = [{ label: 'Session Setting', component: <StudentLayoutTaskPanel /> }]

  // if(session?.type === 'Vizmental'){
  //   tabs.push({ label: "Custom AI Prompts", component: <CustumPromptPanel /> });
  // }

  if (session?.type !== 'Vizmental') {
    tabs.push({ label: 'Groups', component: <GroupPanel /> })
  }

  // One live-monitoring home instead of three separate tabs (Class Performance,
  // Presenter View, Session Dashboard) - see InstructorDashboardPanel.jsx for what's
  // inside (Session Control/Insights/Replay sub-tabs). keepMounted so its Session
  // Control sub-tab's session timer survives switching to a different top-level tab and
  // back. `isActive` tells it whether it's the currently-selected top-level tab, so it
  // doesn't poll telemetry in the background while the instructor is looking elsewhere.
  if (session?.id) {
    tabs.push({
      label: 'Monitor',
      component: <InstructorDashboardPanel sessionId={session.id} isActive={selectedLabel === 'Monitor'} />,
      keepMounted: true,
    })
  }

  tabs.push({ label: 'Task and Unit Test', component: <TaskPanel /> })

  // Position of the selected tab in the list as it is right now; a tab that has just
  // disappeared (Groups) falls back to the first one.
  const selectedIndex = tabs.findIndex((tab) => tab.label === selectedLabel)
  const value = selectedIndex === -1 ? 0 : selectedIndex

  useEffect(() => {
    if (!session) return
    setChatEnable(session?.enable_chat)
  }, [session?.enable_chat])

  // ENABLE AI CHAT, for Python and Vizmental sessions alike (the Session Setting tab relies
  // on this bar for it). On gives students the assistant - for a plain Python session that
  // means converting it to Vizmental, the app's name for Python with AI chat. Off only hides
  // the assistant: it no longer ends the session (End Session does that), and it clears
  // any leftover end-of-session marker (sessionSettings.buildAiChatUpdate).
  const [aiChatSaving, setAiChatSaving] = useState(false)
  const handleAiChatToggle = async (on) => {
    if (!session?.id) return
    setAiChatSaving(true)
    try {
      const result = await saveSessionFields(session.id, buildAiChatUpdate(session.type, on))
      if (result.session) setSession(result.session)
      if (!result.ok) showToast(describeSaveFailure(result), 'error', 5000)
    } finally {
      setAiChatSaving(false)
    }
  }

  // Peer "Chat" switch for Auto Grouping sessions. (The Vizmental AI switch used to share
  // this handler and end the session when turned off; it now uses handleAiChatToggle.)
  const handleChatEnableChange = async () => {
    try {
      const currentChatEnableStatus = await setSessionEnableChat(
        session?.id,
        !chatEnable
      )
      setChatEnable(!!currentChatEnableStatus)
    } catch (error) {
      console.error('Error changing chat enable status: ', error)
    }
  }
  const handleEndSessionWithSurvey = async () => {
    try{
      if(!session?.id) return;
      const ok = await setSessionEnableChatAndCloseSession(
        session.id, false, 'closed with survey'
      );
      if(!ok){console.error("Failed to end session with survey");}
    }catch(error){
      console.error("Error ending session with survey: ", error);
    }
  };

  return (
    // The tab bar stays put and the tab's content scrolls under it. (This root used to be
    // overflowY: hidden, so on a short window the bottom of a tab could not be reached.
    // ExperimentPage.jsx does the same.)
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example">
          {tabs.map((tab, index) => (
            <Tab label={tab.label} {...a11yProps(index)} key={tab.label} />
          ))}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'right' }}>
          {isPythonFamily(session?.type) && (
            <Tooltip
              title={
                <Typography variant="subtitle1">
                  {session?.type === 'Python'
                    ? 'Give students the AI assistant.'
                    : 'Show or hide the AI assistant for students. Turning it off does not end the session - use End Session for that.'}
                </Typography>
              }>
              <FormControlLabel
                value="top"
                label={
                  <Typography
                    sx={{
                      color: '#1976d2',
                      fontWeight: '500',
                      fontSize: '14px',
                    }}>
                    ENABLE AI CHAT
                  </Typography>
                }
                labelPlacement="start"
                control={
                  <Switch
                    className="switch"
                    checked={aiChatEnabled(session)}
                    disabled={aiChatSaving}
                    onChange={(e) => handleAiChatToggle(e.target.checked)}
                    color="primary"
                  />
                }
              />
            </Tooltip>
          )}
          {session?.type == 'Auto Grouping' && (
            <div style={{ display: 'flex' }}>
              <Typography sx={{ marginY: 'auto', marginRight: '1em' }}>
                Passrate {classPassrate}%
              </Typography>
              <TextField
                sx={{ width: '150px' }}
                label="Grouping Threshold (%)"
                variant="filled"
                type="number"
                value={autoGroupingRate}
                onChange={(e) => {
                  setAutoGroupingRate(e.target.value)
                }}
              />
              {session?.grouped && (
                <FormControlLabel
                  value="top"
                  label="Chat"
                  labelPlacement="start"
                  control={
                    <Switch
                      className="switch"
                      checked={chatEnable}
                      onChange={handleChatEnableChange}
                      color="primary"
                    />
                  }
                />
              )}
              {!session?.grouped && (
                <Button
                  className="action-btn"
                  variant="contained"
                  color="primary"
                  onClick={handleNext}>
                  Group Now
                </Button>
              )}
            </div>
          )}
          <Button
            sx={{ marginRight: '20px', marginLeft: '20px' }}
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={() => {
              handleCopyLink()
            }}>
            Distribute
          </Button>
          {session?.type === 'Vizmental' && (
            <Button
              sx={{ marginRight: '20px' }}
              variant="outlined"
              startIcon={<DataObject />}
              onClick={() => setRawDataModalOpen(true)}>
              View Student Data
            </Button>
          )}
          <Button
          sx = {{
            backgroundColor: '#d32f2f', 
            color: 'white', 
            marginRight: '20px',
            '&:hover': {
              backgroundColor: '#b71c1c',
            },
          }}
          varaint = "contained"
          onClick = {handleEndSessionWithSurvey}>
            End Session
          </Button>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {tabs.map((tab, index) => (
          <CustomTabPanel value={value} index={index} key={tab.label} keepMounted={tab.keepMounted}>
            {tab.component}
          </CustomTabPanel>
        ))}
      </Box>

      {/* Raw Data Modal */}
      <RawDataModal 
        open={rawDataModalOpen} 
        onClose={() => setRawDataModalOpen(false)} 
      />
    </Box>
  )
}